import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import type { DeckSlide } from '../deckSlides'

const PAGE_WIDTH = 1440
const PAGE_HEIGHT = 810
const FILE_NAME = 'Snakker-LEO-Pharma-Storyboard.pdf'
/** Long enough for each slide's entrance motion to settle before capture. */
const SETTLE_MS = 1200

type ProgressHandler = (done: number, total: number) => void

/**
 * Renders every slide at full size off-screen, rasterises each one, and saves
 * them as a single landscape PDF — a real file download, no print dialog and no
 * popup window (both are blocked inside the preview sandbox).
 */
export async function downloadStoryboardPdf(
  pages: DeckSlide[],
  onProgress?: ProgressHandler,
): Promise<void> {
  if (typeof document === 'undefined' || pages.length === 0) return

  const host = document.createElement('div')
  host.setAttribute('aria-hidden', 'true')
  Object.assign(host.style, {
    position: 'fixed',
    top: '0px',
    left: '-20000px',
    width: '100vw',
    height: '56.25vw',
    overflow: 'hidden',
    zIndex: '-1',
    pointerEvents: 'none',
    background: '#0C0C0C',
  })
  document.body.appendChild(host)

  const root = createRoot(host)
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'px',
    format: [PAGE_WIDTH, PAGE_HEIGHT],
    compress: true,
  })

  try {
    if (document.fonts?.ready) await document.fonts.ready

    for (let index = 0; index < pages.length; index += 1) {
      root.render(createElement(pages[index].component))
      await waitFrames(3)
      await waitForImages(host)
      await delay(SETTLE_MS)

      const canvas = await html2canvas(host, {
        backgroundColor: '#0C0C0C',
        useCORS: true,
        allowTaint: true,
        scale: 1.5,
        logging: false,
      })

      if (index > 0) pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT], 'landscape')
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.92),
        'JPEG',
        0,
        0,
        PAGE_WIDTH,
        PAGE_HEIGHT,
      )
      onProgress?.(index + 1, pages.length)
    }

    pdf.save(FILE_NAME)
  } finally {
    root.unmount()
    host.remove()
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function waitFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count
    const step = () => {
      remaining -= 1
      if (remaining <= 0) resolve()
      else window.requestAnimationFrame(step)
    }
    window.requestAnimationFrame(step)
  })
}

function waitForImages(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  return Promise.all(
    images.map((image) =>
      image.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            image.addEventListener('load', () => resolve(), { once: true })
            image.addEventListener('error', () => resolve(), { once: true })
          }),
    ),
  ).then(() => undefined)
}
